import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useWebFunnelDefinitions, useWebFunnelReport } from '@/hooks/useWebFunnelReport';
import { FunnelSankey } from '@/components/webGrowth/FunnelSankey';
import { cn } from '@/lib/utils';

const tooltipStyle = {
    background: 'hsl(222 47% 9%)',
    border: '1px solid hsl(222 30% 16%)',
    borderRadius: '8px',
    fontSize: '12px',
};

const COLORS = ['hsl(var(--primary))', 'hsl(172 66% 50%)', 'hsl(38 92% 50%)', 'hsl(142 71% 45%)', 'hsl(262 83% 58%)'];

export function WebFunnelPanel({ days }: { days: number }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const funnelFromUrl = searchParams.get('funnel');
    const { data: definitions, isLoading: defsLoading } = useWebFunnelDefinitions();
    const [definitionId, setDefinitionId] = useState<string | null>(null);

    useEffect(() => {
        if (!definitions?.length) return;
        const match =
            definitions.find((d) => d.id === funnelFromUrl)
            ?? definitions.find((d) => d.id === 'licenses_main_v1')
            ?? definitions[0];
        setDefinitionId(match.id);
    }, [definitions, funnelFromUrl]);

    const { data: report, isLoading: reportLoading, error } = useWebFunnelReport(definitionId, days);

    const chartData = useMemo(
        () => (report?.steps ?? []).map((step) => ({
            name: step.label,
            users: step.active_users,
            pct: step.pct_of_first,
        })),
        [report?.steps],
    );

    if (defsLoading) return <Loader2 className="animate-spin text-muted-foreground" size={18} />;

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-2">
                {(definitions ?? []).map((def) => (
                    <button
                        key={def.id}
                        type="button"
                        onClick={() => {
                            setDefinitionId(def.id);
                            const next = new URLSearchParams(searchParams);
                            next.set('tab', 'funnels');
                            next.set('funnel', def.id);
                            setSearchParams(next, { replace: true });
                        }}
                        className={cn(
                            'rounded-lg px-3 py-1.5 text-sm font-medium border',
                            definitionId === def.id
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-border text-muted-foreground hover:text-foreground',
                        )}
                    >
                        {def.label}
                    </button>
                ))}
            </div>

            {definitionId === 'licenses_main_v1' && (
                <p className="text-sm text-muted-foreground">
                    Operación diaria:{' '}
                    <Link to="/licenses" className="text-primary hover:underline">
                        panel Licencias
                    </Link>
                    {' · '}
                    Definición versionada en BD (<code className="text-xs">web_funnel_definition</code>).
                </p>
            )}

            {reportLoading && <Loader2 className="animate-spin text-muted-foreground" size={18} />}
            {error && (
                <p className="text-sm text-rose-500">
                    {error instanceof Error ? error.message : 'Error cargando embudo.'}
                </p>
            )}

            {report && (
                <>
                    <p className="text-sm text-muted-foreground">{report.note}</p>

                    <section className="rounded-xl border border-border bg-card p-4">
                        <h3 className="text-sm font-semibold">Embudo lineal — {report.label}</h3>
                        <div className="mt-3 h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={56} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip
                                        contentStyle={tooltipStyle}
                                        formatter={(value: number, _name, item) => {
                                            const pct = item.payload.pct;
                                            return [
                                                `${value.toLocaleString('es-ES')} usuarios${pct != null ? ` (${pct} % del 1.º paso)` : ''}`,
                                                'Usuarios',
                                            ];
                                        }}
                                    />
                                    <Bar dataKey="users" radius={[6, 6, 0, 0]}>
                                        {chartData.map((_, index) => (
                                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </section>

                    <section className="rounded-xl border border-border bg-card p-4">
                        <h3 className="text-sm font-semibold">Flujo entre pasos (Sankey)</h3>
                        <p className="mt-1 text-xs text-muted-foreground">
                            Basado en eventos <code>funnel_transition</code> (landing). Grosor ≈ usuarios activos.
                        </p>
                        <div className="mt-4">
                            <FunnelSankey steps={report.steps} transitions={report.transitions} />
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}
